import { cloneElement, isValidElement, useEffect, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { CoachMarkV2 } from 'app/KinerjaShared/components/CoachMarkV2'

import type { CoachMarkV2Step } from 'app/KinerjaShared/components/CoachMarkV2'

type OnboardingPropsBase = TriggerProps & {
  steps: Array<CoachMarkV2Step>
  onClick?: () => void
}

type OnboardingPropsWithAlways = OnboardingPropsBase & {
  openOnMount: 'ALWAYS'
  storageKey?: never
}

// Ensure the storageKey is provided when openOnMount is 'ONCE'
type OnboardingPropsWithOnce = OnboardingPropsBase & {
  openOnMount: 'ONCE'
  storageKey: string
}

type OnboardingPropsWithNever = OnboardingPropsBase & {
  openOnMount?: never
  storageKey?: never
}

export type OnboardingProps =
  | OnboardingPropsWithAlways
  | OnboardingPropsWithOnce
  | OnboardingPropsWithNever

export function Onboarding({
  storageKey,
  children,
  asChild,
  steps,
  openOnMount,
  onClick,
}: OnboardingProps) {
  const [isOpen, setIsOpen] = useState(false)

  // --------------------------
  // Handle initial open logic
  // --------------------------
  useEffect(() => {
    if (openOnMount === 'ALWAYS') {
      setIsOpen(true)
    } else if (openOnMount === 'ONCE') {
      // Check if onboarding has been shown using localStorage
      const hasBeenShown = localStorage.getItem(storageKey)

      if (!hasBeenShown) {
        setIsOpen(true)
        localStorage.setItem(storageKey, 'true')
      }
    }
  }, [openOnMount, steps, storageKey])

  const handleOpen = () => {
    onClick?.()
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <>
      <Trigger asChild={asChild} onClick={handleOpen}>
        {children}
      </Trigger>
      <CoachMarkV2
        isOpen={isOpen}
        tooltipSeparation={12}
        // Moving target is enabled in order to track the elements. Without
        // this, the coach mark will not follow correctly positioned tooltip
        // and mask element. Behind the scene, it sets up a timer with
        // updateInterval as the interval.
        movingTarget={true}
        updateInterval={10}
        steps={steps}
        resetOnClose
        customDescriptionRenderer={(description, { stepIndex, allSteps }) => {
          return (
            <div className="space-y-2">
              <Text variant="heading-md">
                {stepIndex + 1}/{allSteps.length}
              </Text>
              <Text variant="body-lg">{description}</Text>
            </div>
          )
        }}
        disableAutoScroll={true}
        maskPadding={0}
        onClose={handleClose}
      />
    </>
  )
}

type TriggerProps = {
  asChild?: boolean
  children?: React.ReactNode
  onClick?: () => void
}

function Trigger({ asChild = false, children, onClick }: TriggerProps) {
  // If asChild is true, clone the child element and add onClick handler
  if (asChild && children) {
    if (!isValidElement(children)) {
      throw new Error(
        'Onboarding.Trigger with asChild prop requires a valid React element as children'
      )
    }

    return cloneElement(children, {
      onClick,
      ...children.props,
    })
  }

  // Default button implementation
  return (
    <Button variant="ghost" size="lg" onClick={onClick}>
      {children}
    </Button>
  )
}
